/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import SimpleInput from "./SimpleInput";
import { AutoCompleteOptions } from "../../models/AutoCompleteOptions";
import { InventoryItemUnit } from "../../models/inventory/item/InventoryItemUnit";
import NumberFormatter from "../../util/NumberFormatter";
import { quantityUnits } from "../../common/Strings/Strings";

interface Props {
  disabled?: boolean;
  error?: FieldError;
  label?: string;
  onChange: (price?: number) => void;
  readOnly?: boolean;
  required?: boolean;
  unit?: InventoryItemUnit;
  value?: number;
}

const MultiplicationFieldsComponent = ({
  disabled,
  error,
  label,
  onChange,
  readOnly,
  unit,
  value,
}: Props) => {
  const [multiplier1, setMultiplier1] = useState<number>();
  const [multiplier2, setMultiplier2] = useState<number>();
  const [multiplier3, setMultiplier3] = useState<number>();
  const [block, setBlock] = useState<boolean>(true);

  useEffect(() => {
    /* Fill multiplier fields properly when default values are used. */
    const localMultiplier1 = multiplier1 === undefined ? 1 : multiplier1;
    const localMultiplier2 = multiplier2 === undefined ? 1 : multiplier2;
    const localMultiplier3 = multiplier3 === undefined ? 1 : multiplier3;

    if (
      block &&
      value !== undefined &&
      value !== localMultiplier1 * localMultiplier2 * localMultiplier3
    ) {
      setMultiplier3(value);
      setMultiplier2(1);
      setMultiplier1(1);
    }
  }, [value]);

  const update = (m1: number | undefined, m2: number | undefined, m3: number | undefined) => {
    if (block) {
      setBlock(false);
    }

    const localMultiplier1 = m1 === undefined ? 1 : m1;
    const localMultiplier2 = m2 === undefined ? 1 : m2;
    const localMultiplier3 = m3 === undefined ? 1 : m3;

    onChange(localMultiplier1 * localMultiplier2 * localMultiplier3);
  };

  return (
    <div className={error ? "has-error" : ""}>
      <label>{label}</label>
      <div className="flex space-x-4 items-center">
        <SimpleInput
          autoComplete={AutoCompleteOptions.off}
          disabled={disabled}
          labelOff
          name="multiplier1"
          onChange={(v: string) => {
            setMultiplier1(v.length === 0 ? undefined : Number(v));
            update(Number(v), multiplier2, multiplier3);
          }}
          readOnly={readOnly}
          type="number"
          value={multiplier1}
          width="4rem"
        />
        <div>x</div>
        <SimpleInput
          autoComplete={AutoCompleteOptions.off}
          disabled={disabled}
          labelOff
          name="multiplier2"
          onChange={(v: string) => {
            setMultiplier2(v.length === 0 ? undefined : Number(v));
            update(multiplier1, Number(v), multiplier3);
          }}
          readOnly={readOnly}
          type="number"
          value={multiplier2}
          width="4rem"
        />
        <div>x</div>
        <SimpleInput
          autoComplete={AutoCompleteOptions.off}
          disabled={disabled}
          labelOff
          name="multiplier3"
          onChange={(v: string) => {
            setMultiplier3(v.length === 0 ? undefined : Number(v));
            update(multiplier1, multiplier2, Number(v));
          }}
          readOnly={readOnly}
          type="number"
          value={multiplier3}
          width="4rem"
        />
        <div>=</div>
        <div>
          {value !== undefined ? NumberFormatter.format(value) : 1}{" "}
          {quantityUnits[unit || ""]}
        </div>
      </div>{" "}
    </div>
  );
};

export default MultiplicationFieldsComponent;
